import {request} from '@umijs/max';
import {apiPath} from '@/utils/apiPath';

type ApiResponse<T> = {
  code?: string;
  message?: string;
  data?: T;
};

type LoginPasswordResponse = {
  require_2fa?: boolean;
  temp_token?: string;
  userName?: string;
};

type VerifyOtpResponse = {
  success?: boolean;
  id_token?: string;
};

type AccountResponse = {
  id?: string;
  login?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  imageUrl?: string;
  activated?: boolean;
  langKey?: string;
  createdDate?: string;
  lastModifiedDate?: string;
  roleIds?: string[];
  permissions?: string[];
};

type AccountViewModel = AccountResponse & {
  name?: string;
  avatar?: string;
};

function unwrapApiData<T>(payload: ApiResponse<T> | T | null | undefined): T | undefined {
  if (!payload) return undefined;
  if (typeof payload === 'object' && 'data' in payload) {
    return (payload as ApiResponse<T>).data;
  }
  return payload as T;
}

function mapAccount(account: AccountResponse): AccountViewModel {
  const fullName = `${account.firstName || ''} ${account.lastName || ''}`.trim();
  return {
    ...account,
    name: fullName || account.login || '',
    avatar: account.imageUrl || '',
  };
}

export async function loginPassword(payload: { username: string; password: string }) {
  const response = await request<ApiResponse<LoginPasswordResponse> | LoginPasswordResponse>(apiPath('/authenticate'), {
    method: 'POST',
    data: payload,
    skipErrorHandler: true,
  });
  return unwrapApiData(response) ?? {};
}

export async function verifyOtp(payload: { otp: string; tempToken: string }) {
  const response = await request<ApiResponse<VerifyOtpResponse> | VerifyOtpResponse>(apiPath('/authenticate/2fa-verify'), {
    method: 'POST',
    data: payload,
    skipErrorHandler: true,
  });
  return unwrapApiData(response) ?? {};
}

export async function changeExpiredPassword(payload: {
  email: string;
  currentPassword: string;
  newPassword: string;
  otp: string;
}) {
  const response = await request<ApiResponse<VerifyOtpResponse> | VerifyOtpResponse>(
    apiPath('/authenticate/change-expired-password'),
    {
      method: 'POST',
      data: payload,
      skipErrorHandler: true,
    },
  );
  return unwrapApiData(response) ?? {};
}

export async function updateMyAccount(payload: {
  firstName?: string;
  lastName?: string;
  email?: string;
  langKey?: string;
  imageUrl?: string;
}) {
  const response = await request<ApiResponse<AccountResponse> | AccountResponse>(apiPath('/account'), {
    method: 'POST',
    data: payload,
  });
  const account = unwrapApiData(response) ?? {};
  return mapAccount(account);
}

export async function changeMyPassword(payload: {
  currentPassword: string;
  newPassword: string;
  otp: string;
}) {
  const response = await request<ApiResponse<Record<string, unknown>> | Record<string, unknown>>(
    apiPath('/account/change-password'),
    {
      method: 'POST',
      data: payload,
    },
  );
  return unwrapApiData(response) ?? {};
}

export async function logoutApi() {
  return request<void>(`/auth/logout`, {method: 'POST'});
}

export async function meApi() {
  const response = await request<ApiResponse<AccountResponse> | AccountResponse>(apiPath('/account'), {method: 'GET'});
  const account = unwrapApiData(response) ?? {};
  return mapAccount(account);
}
